// ============================================================================
// LE CHOIX D'UN APPAREIL AU PARC — un seul sélecteur pour tous les parcours qui affectent du matériel.
//
// UTILITÉ : l'assistant de création (« Première caisse », « Mobile Partner ») et l'assistant d'équipement de la fiche
// magasin choisissaient chacun leur appareil dans une liste écrite à part : l'une cherchait, l'autre non ; l'une
// montrait le nom donné au parc, l'autre seulement la série. Deux listes du même geste divergent — il n'y en a plus
// qu'une.
// ⚠️ LA RECHERCHE PORTE SUR CE QU'ON A SOUS LES YEUX : le nom au parc (« Chalet 5 caisse 1 »), la série lue sur
//    l'étiquette, le modèle, le type, l'identifiant.
// ⚠️ LE RETENU RESTE EN TÊTE : cocher puis filtrer ne doit jamais faire disparaître la sélection de l'écran.
// ⚠️ LA FORME DE LA CASE DIT LE NOMBRE : ronde pour un seul appareil, carrée quand on peut en cocher plusieurs.
// ============================================================================
import { useMemo, useState, type ReactNode } from 'react';
import { dateTimeLong } from '@orgazone/date-format';
import { EmptyState } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import type { DeviceRow } from '../api';
import { DeviceName } from '../device-name';

/** Ce qu'une recherche compare : tout ce qui désigne l'appareil à l'écran ou sur son étiquette. */
export function deviceSearchText(d: Pick<DeviceRow, 'name' | 'serial' | 'model' | 'family' | 'id'>): string {
  return [d.name, d.serial, d.model, d.family, d.id].filter(Boolean).join(' ').toLowerCase();
}

/** Les appareils à montrer : le retenu d'abord, puis ceux que la recherche laisse passer. */
export function pickerList(devices: readonly DeviceRow[], selected: readonly string[], query: string): DeviceRow[] {
  const q = query.trim().toLowerCase();
  return [
    ...devices.filter((d) => selected.includes(d.id)),
    ...devices.filter((d) => !selected.includes(d.id) && (!q || deviceSearchText(d).includes(q))),
  ];
}

export function DevicePicker({ devices, total, selected, onToggle, multiple = false, title, icon, emptyPoolNote, selectedNote, footer }: {
  /** Les appareils DISPONIBLES de la nature voulue. */
  devices: DeviceRow[];
  /** Le parc entier de cette nature, pour dire « N sur M » — absent : seul le disponible est compté. */
  total?: number;
  selected: string[];
  onToggle: (id: string) => void;
  multiple?: boolean;
  title: string;
  icon: IconName;
  /** Ce que l'état vide dit quand le parc n'a rien de disponible. */
  emptyPoolNote: string;
  /** Ce que la ligne retenue annonce (« retenue · caisse n° 1 en location longue »). */
  selectedNote?: (d: DeviceRow) => string;
  /** Ce qui suit la liste quand un appareil est retenu (le régime, les dates). */
  footer?: ReactNode;
}) {
  const [query, setQuery] = useState('');
  const list = useMemo(() => pickerList(devices, selected, query), [devices, selected, query]);
  const count = `${selected.length ? `${selected.length} retenu${selected.length > 1 ? 's' : ''} · ` : ''}${devices.length} disponible${devices.length > 1 ? 's' : ''}${total != null ? ` sur ${total}` : ''} au parc`;

  return (
    <div style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div style={{ padding: '11px 15px', borderBottom: '1px solid var(--ink200)', background: 'var(--head, var(--ink-50))', display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>{title}</span>
        <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>{count}</span>
        <div style={{ flex: 1 }} />
        <input aria-label="Rechercher un appareil" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nom, n° de série ou modèle" className="mono"
          style={{ width: 210, height: 30, padding: '0 11px', borderRadius: 'var(--r-xs)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-note)' }} />
      </div>
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {list.map((d) => {
          const on = selected.includes(d.id);
          return (
            <button key={d.id} type="button" onClick={() => onToggle(d.id)} aria-pressed={on} data-device-option
              style={{ display: 'flex', alignItems: 'flex-start', gap: 11, width: '100%', padding: '11px 15px', border: 'none', borderBottom: '1px solid var(--ink100)', background: on ? 'var(--ok-bg)' : 'transparent', color: 'var(--ink900)', cursor: 'pointer', textAlign: 'left' }}>
              {multiple
                ? <span aria-hidden="true" style={{ width: 17, height: 17, borderRadius: 'var(--r-r5)', flexShrink: 0, marginTop: 2, display: 'grid', placeItems: 'center', fontSize: 'var(--t-micro)', color: 'var(--cta-ink)', background: on ? 'var(--teal-500)' : 'transparent', border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink300)'}` }}>{on ? '✓' : ''}</span>
                : <span aria-hidden="true" style={{ width: 15, height: 15, borderRadius: 'var(--r-pill)', flexShrink: 0, marginTop: 3, border: `2px solid ${on ? 'var(--teal-500)' : 'var(--ink300)'}`, background: on ? 'var(--teal-500)' : 'transparent', boxShadow: on ? 'inset 0 0 0 2.5px var(--paper)' : 'none' }} />}
              <span style={{ minWidth: 0, flex: 1 }}>
                {/* Le nom qu'on lui a donné au parc, puis son modèle (ou son type) : ce qu'on lit pour choisir. */}
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}><DeviceName d={d} /></span>
                  <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>{d.model ?? d.family}</span>
                </span>
                {/* ⚠️ Le n° de série est AFFICHÉ, jamais saisi : il arme le contrôle anti-substitution au scan. Son
                    absence se DIT — sans elle, n'importe quelle tablette peut consommer le QR. */}
                <span className="mono" style={{ display: 'block', fontSize: 'var(--t-sub)', color: d.serial ? 'var(--ink500)' : 'var(--wa-fg)', marginTop: 3 }}>
                  {d.serial ? `SN ${d.serial}` : 'aucun n° de série au parc — le contrôle anti-substitution ne jouera pas'} · {d.id.slice(0, 8)}
                  {d.lastSeenAt ? ` · dernier contact ${dateTimeLong(d.lastSeenAt)}` : ' · jamais mis en service'}
                </span>
                {on && selectedNote && <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ok-fg)', marginTop: 3 }}>{selectedNote(d)}</span>}
              </span>
            </button>
          );
        })}
        {list.length === 0 && (
          <EmptyState compact icon={icon} title={devices.length === 0 ? 'Aucun appareil disponible' : 'Aucun appareil proposé'}
            note={devices.length === 0 ? emptyPoolNote : 'Aucun appareil ne correspond à cette recherche.'} />
        )}
      </div>
      {footer}
    </div>
  );
}
